import { useEffect, useState } from 'react'
import isEqual from 'lodash/isEqual'

interface Position {
  top: number
  right: number
  bottom: number
  left: number
  width: number
  height: number
}

interface Options {
  update?: 'none' | 'event' | 'live'
}

function getPosition(element: HTMLElement | undefined): Position | undefined {
  if (!element) return

  const { top, right, bottom, left, width, height } = element.getBoundingClientRect()
  const rect = {
    top,
    right,
    bottom,
    left,
    width,
    height
  }
  return rect
}

/**
 * Provides the position and dimensions of the given element.
 *
 * Options:
 *   - update
 *     - none: never update, only provide an initial value (default)
 *     - event: update based on events like the browser scrolling and resizing (recommended)
 *     - live: update continuously, catches all changes
 */
export function useElementPosition(element?: HTMLElement, options?: Options): Position | undefined {
  const [position, setPosition] = useState<Position | undefined>(() => getPosition(element))

  useEffect(() => {
    function updatePosition(): void {
      setPosition(position => {
        const newPosition = getPosition(element)
        if (isEqual(newPosition, position)) {
          return position
        } else {
          return newPosition
        }
      })
    }

    // Do an initial update to handle the element itself being defined/undefined
    updatePosition()
    if (!element) return

    if (options?.update === 'event') {
      window.addEventListener('resize', updatePosition)
      window.addEventListener('scroll', updatePosition)

      return () => {
        window.removeEventListener('resize', updatePosition)
        window.removeEventListener('scroll', updatePosition)
      }
    }

    if (options?.update === 'live') {
      let handle: number
      const onRender = (): void => {
        updatePosition()
        handle = window.requestAnimationFrame(onRender)
      }
      handle = window.requestAnimationFrame(onRender)
      return () => {
        window.cancelAnimationFrame(handle)
      }
    }
  }, [element, options?.update])

  return position
}
